// file-utils.service.ts
import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class FileUtilsService {
  readonly MAX_FILE_COUNT = 10;

  extractValidDroppedFiles(event: DragEvent, currentFiles: File[]): File[] | null {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return null;

    const newFiles = Array.from(files);

    if (!this.canAddFiles(currentFiles, newFiles)) {
      return null;
    }

    return newFiles;
  }

  extractValidSelectedFiles(event: Event, currentFiles: File[]): File[] | null {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return null;

    const newFiles = Array.from(input.files);

    if (!this.canAddFiles(currentFiles, newFiles)) {
      return null;
    }

    return newFiles;
  }

  canAddFiles(currentFiles: File[], newFiles: File[]): boolean {
    return currentFiles.length + newFiles.length <= this.MAX_FILE_COUNT;
  }

  fileSizeValidator(maxSize: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const files = control.value as File[];
      if (!files || files.length === 0) return null;
      const invalidFiles = files.filter(file => file.size > maxSize);
      return invalidFiles.length > 0
        ? { maxFileSize: { message: `${invalidFiles.length} 個のファイルが ${maxSize / (1024 * 1024)}MB を超えています` } }
        : null;
    };
  }

  minFileCountValidator(minCount: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const files = control.value;
      if (!Array.isArray(files) || files.length < minCount) {
        return { minFileCount: { required: minCount, actual: files?.length || 0, message: `少なくとも ${minCount}個のファイルをアップロードしてください` } };
      }
      return null;
    };
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  addFiles(existingFiles: File[], newFiles: File[], callback: (files: File[]) => void): void {
    const uniqueFiles = [...existingFiles];
    newFiles.forEach(file => {
      if (!uniqueFiles.some(f => f.name === file.name && f.size === file.size)) {
        uniqueFiles.push(file);
      }
    });
    callback(uniqueFiles);
  }

  removeFile(files: File[], index: number, callback: (files: File[]) => void): void {
    files.splice(index, 1);
    callback([...files]);
  }

  downloadAttachment(res: HttpResponse<Blob>): void {
    const contentDisposition = res.headers.get('Content-Disposition');
    const filename = this.extractFilename(contentDisposition);

    const blob = new Blob([res.body!], { type: res.body!.type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  extractFilename(contentDisposition: string | null): string {
    if (!contentDisposition) return 'download.bin';
    const match = contentDisposition.match(/filename\*=UTF-8''(.+)|filename="?([^;"]+)/);
    if (match) {
      return decodeURIComponent(match[1] || match[2]);
    }
    return 'download.bin';
  }
}
